import { AlertTriangle, Announcement02, Globe01, HelpCircle, Link03, VideoRecorder } from "@untitledui/icons";
import { CheckCircle, Loader2 } from "lucide-react";
import { Link as AriaLink } from "react-aria-components";
import { BadgeWithIcon } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";

export type ConnectionRowState = "disconnected" | "incomplete" | "redirecting" | "connected";

export type ConnectionRowStats = {
  liveCampaigns: number | null;
  videosInAds: number | null;
};

export type ConnectionRowAccount = {
  advertiserId: string;
  name: string | null;
  region: string | null;
};

interface ConnectionRowProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  state: ConnectionRowState;
  isLoading: boolean;
  onConnect: () => void;
  disabled?: boolean;
  incompleteLabel?: string;
  incompleteMessage?: string;
  connectedMessage?: string;
  manageUrl?: string;
  guideTooltip?: string;
  account?: ConnectionRowAccount | null;
  stats?: ConnectionRowStats | null;
  highlight?: boolean;
}

const Divider = () => (
  <span aria-hidden className="text-quaternary">
    |
  </span>
);

const AccountLine = ({ account }: { account: ConnectionRowAccount }) => (
  <p className="mt-4 flex flex-wrap items-center gap-2 text-sm text-tertiary">
    {account.name && <span className="font-medium text-primary">{account.name}</span>}
    {account.name && <Divider />}
    <span className="whitespace-nowrap">
      <span className="font-semibold text-primary">ID</span> {account.advertiserId}
    </span>
    {account.region && (
      <>
        <Divider />
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          <Globe01 className="h-3.5 w-3.5 text-fg-tertiary" aria-hidden />
          {account.region}
        </span>
      </>
    )}
  </p>
);

const StatsLine = ({ stats }: { stats: ConnectionRowStats }) => {
  const parts: Array<{ key: string; icon: typeof VideoRecorder; value: number; label: string }> = [];
  if (stats.liveCampaigns !== null) {
    parts.push({
      key: "campaigns",
      icon: Announcement02,
      value: stats.liveCampaigns,
      label: stats.liveCampaigns === 1 ? "live campaign" : "live campaigns",
    });
  }
  if (stats.videosInAds !== null && stats.videosInAds > 0) {
    parts.push({
      key: "videos",
      icon: VideoRecorder,
      value: stats.videosInAds,
      label: stats.videosInAds === 1 ? "video in ads" : "videos in ads",
    });
  }
  if (parts.length === 0) return null;

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-tertiary">
      {parts.map((part, index) => {
        const Icon = part.icon;
        return (
          <span key={part.key} className="inline-flex items-center gap-2">
            {index > 0 && <Divider />}
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <Icon className="h-4 w-4 text-fg-brand-primary" aria-hidden />
              <span className="font-semibold text-primary">{part.value}</span>
              {part.label}
            </span>
          </span>
        );
      })}
    </div>
  );
};

export const ConnectionRow = ({
  icon: Icon,
  title,
  description,
  state,
  isLoading,
  onConnect,
  disabled,
  incompleteLabel,
  incompleteMessage,
  connectedMessage,
  manageUrl,
  guideTooltip,
  account,
  stats,
  highlight,
}: ConnectionRowProps) => {
  return (
    <div
      className={`flex items-start gap-4 p-4 transition-colors duration-1000 ${highlight ? "bg-success-secondary" : ""}`}
    >
      <div className="flex-shrink-0 w-10 h-10 rounded-lg border border-border-primary bg-white flex items-center justify-center">
        <Icon className="w-5 h-5 text-fg-tertiary" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-md font-medium text-text-primary flex items-center gap-1.5">
          {title}
          {guideTooltip && (
            <Tooltip title="Access required" description={guideTooltip} placement="top">
              <TooltipTrigger
                aria-label={`What you'll need to authorize for ${title}`}
                className="inline-flex cursor-help text-fg-quaternary hover:text-fg-tertiary"
              >
                <HelpCircle className="h-4 w-4" />
              </TooltipTrigger>
            </Tooltip>
          )}
        </p>
        <p className="text-sm text-text-tertiary">{description}</p>
        {state === "connected" && account && <AccountLine account={account} />}
        {state === "connected" && stats && <StatsLine stats={stats} />}
      </div>

      <div aria-live="polite" className="flex-shrink-0 flex items-center gap-3">
        {isLoading ? (
          <Button color="primary" size="md" isDisabled>
            <Loader2 className="w-4 h-4 animate-spin" />
          </Button>
        ) : (
          <>
            {state === "incomplete" && incompleteMessage && (
              <Tooltip title={incompleteMessage} placement="left">
                <TooltipTrigger className="inline-flex cursor-help">
                  <BadgeWithIcon color="warning" iconLeading={AlertTriangle}>
                    {incompleteLabel ?? "We're missing some permissions"}
                  </BadgeWithIcon>
                </TooltipTrigger>
              </Tooltip>
            )}
            {state === "connected" &&
              (manageUrl ? (
                <Tooltip title={connectedMessage ?? "Manage access on TikTok"} placement="left">
                  <AriaLink
                    href={manageUrl}
                    className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-[#ecfdf3] px-3 py-2 text-sm font-semibold text-[#079455] outline-brand transition-colors duration-100 hover:bg-[#d1fadf] focus-visible:outline-2 focus-visible:outline-offset-2"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Connected
                  </AriaLink>
                </Tooltip>
              ) : (
                <div className="flex items-center gap-1 px-3 py-2 rounded-lg bg-[#ecfdf3]">
                  <CheckCircle className="w-5 h-5 text-[#079455]" />
                  <span className="text-sm font-semibold text-[#079455]">Connected</span>
                </div>
              ))}
            {state === "redirecting" && (
              <Button color="secondary" size="md" isDisabled iconLeading={Loader2}>
                Redirecting...
              </Button>
            )}
            {(state === "disconnected" || state === "incomplete") && (
              <Button color="primary" size="md" onClick={onConnect} isDisabled={disabled} iconLeading={Link03}>
                Authorise on TikTok
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};
